import { useState, useMemo } from 'react';
import type { Payment } from '../types/payment.types';
import type { PaymentFilterValues } from '../types/paymentFilter.types';

export function usePaymentFiltering(allPayments: Payment[]) {
  const [activeFilter, setActiveFilter] = useState<string>('all');
  const [dateRange, setDateRange] = useState<string>('this-month');
  const [appliedFilters, setAppliedFilters] = useState<PaymentFilterValues | null>(null);

  const payments = useMemo(() => {
    if (!allPayments) return [];

    let filtered = allPayments.filter((payment) => {
      if (activeFilter === 'all') return true;
      return payment.status === activeFilter;
    });

    if (appliedFilters) {
      filtered = filtered.filter((payment) => {
        if (appliedFilters.campaign && payment.campaignId !== appliedFilters.campaign) {
          return false;
        }
        if (appliedFilters.status && payment.status !== appliedFilters.status) {
          return false;
        }
        const amount = Number(payment.baseFee) || 0;
        if (amount < appliedFilters.amountRange[0] || amount > appliedFilters.amountRange[1]) {
          return false;
        }
        if (appliedFilters.paymentMode && payment.paymentMode !== appliedFilters.paymentMode) {
          return false;
        }
        if (appliedFilters.dueDateFrom && payment.dueDate) {
          if (new Date(payment.dueDate) < new Date(appliedFilters.dueDateFrom)) {
            return false;
          }
        }
        if (appliedFilters.dueDateTo && payment.dueDate) {
          if (new Date(payment.dueDate) > new Date(appliedFilters.dueDateTo)) {
            return false;
          }
        }
        if (appliedFilters.hasInvoice !== null && appliedFilters.hasInvoice !== undefined) {
          if (payment.hasInvoice !== appliedFilters.hasInvoice) {
            return false;
          }
        }
        if (appliedFilters.influencer) {
          const influencerName = payment.influencer?.name?.toLowerCase() || '';
          if (!influencerName.includes(appliedFilters.influencer.toLowerCase())) {
            return false;
          }
        }
        return true;
      });
    }

    return filtered;
  }, [allPayments, activeFilter, appliedFilters]);

  const handleApplyFilters = (filters: PaymentFilterValues) => {
    setAppliedFilters(filters);
  };

  const handleClearFilters = () => {
    setAppliedFilters(null);
  };

  return {
    activeFilter,
    setActiveFilter,
    dateRange,
    setDateRange,
    appliedFilters,
    handleApplyFilters,
    handleClearFilters,
    payments,
  };
}
